/**
 * 文件描述：统一加载平台品牌配置，并按租户应用已审核的代理商品牌。
 * @author DIY
 * @createTime 2026-07-24
 */
import { getPortalSiteConfig } from '@/api/portal'
import { resolveBrand } from '@/api/system/agent'
import { getTenantId } from '@/utils/auth'
import defaultLogo from '@/assets/portal/home/brand-logo-blue.png'
import defaultLoginLogo from '@/assets/portal/home/brand-logo-white.png'

// 平台未配置品牌时使用的统一默认值。
const DEFAULT_BRAND_NAME = '紫金山AI'
const brandName = ref(DEFAULT_BRAND_NAME)
const brandLogo = ref(defaultLogo)
const loginLogo = ref(defaultLoginLogo)
const loadedTenantId = ref<number | null>()
let loadingPromise: Promise<void> | null = null

/** 加载平台品牌，并使用当前租户已审核的代理商品牌覆盖。 */
const loadSystemBrand = async (force = false) => {
  const tenantId = Number(getTenantId()) || null
  if (!force && loadedTenantId.value === tenantId) return
  if (loadingPromise) return loadingPromise

  loadingPromise = (async () => {
    const [siteResult, agentResult] = await Promise.allSettled([
      getPortalSiteConfig(),
      tenantId ? resolveBrand(tenantId) : Promise.resolve(null)
    ])
    const siteConfig = siteResult.status === 'fulfilled' ? siteResult.value : null
    const agentBrand = agentResult.status === 'fulfilled' ? agentResult.value : null

    brandName.value = agentBrand?.brandName || siteConfig?.siteName || DEFAULT_BRAND_NAME
    const nextLogo = agentBrand?.logoUrl || siteConfig?.logo || ''
    // 远程 logo 失效时不要留下破图；回退本地默认图
    brandLogo.value = isUsableImageUrl(nextLogo) ? nextLogo : defaultLogo
    const nextLoginLogo =
      agentBrand?.loginLogoUrl || siteConfig?.loginLogo || agentBrand?.logoUrl || ''
    loginLogo.value = isUsableImageUrl(nextLoginLogo) ? nextLoginLogo : defaultLoginLogo
    loadedTenantId.value = tenantId
  })().finally(() => {
    loadingPromise = null
  })
  return loadingPromise
}

const isUsableImageUrl = (url: string) => {
  const value = String(url || '').trim()
  if (!value) return false
  // 排除明显无效占位
  if (/^(\?+|null|undefined)$/i.test(value)) return false
  return true
}

/** 提供全局共享的响应式品牌信息。 */
export const useSystemBrand = () => ({ brandName, brandLogo, loginLogo, loadSystemBrand })
